import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  EyeIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  FunnelIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { requestAPI, mutualisteAPI, policyAPI, reasonsAPI, requestTypeAPI, formatCurrency, formatDateTime, getFileUrl } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const AdminRequests = () => {
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processAction, setProcessAction] = useState(null);
  const [processingNotes, setProcessingNotes] = useState('');
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState('');
  const [treatmentReasonId, setTreatmentReasonId] = useState('');
  const [withdrawalPreview, setWithdrawalPreview] = useState(null);
  const [isWithdrawalPreviewLoading, setIsWithdrawalPreviewLoading] = useState(false);
  const [withdrawalContext, setWithdrawalContext] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    mutualiste: '',
    startDate: '',
    endDate: ''
  });
  const [mutualisteSearch, setMutualisteSearch] = useState('');
  const [mutualistes, setMutualistes] = useState([]);
  const [showMutualisteDropdown, setShowMutualisteDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const mutualisteRef = useRef(null);

  // Récupérer les demandes
  const { data: requestsData, isLoading, refetch: refetchRequests } = useQuery(
    ['admin-requests', filters, currentPage],
    () => requestAPI.getRequests({ 
      status: filters.status || undefined,
      type: filters.type || undefined,
      mutualisteId: filters.mutualiste || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      page: currentPage,
      limit: 20
    }),
    {
      refetchInterval: 30000,
      // Forcer un refetch à chaque montage et à chaque retour sur l'onglet
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const requests = requestsData?.data?.data?.requests || [];
  const pagination = requestsData?.data?.data?.pagination || {};

  // Types de demandes (RequestType) utilisés pour alimenter le filtre "Type de demande"
  const { data: requestTypesData } = useQuery(
    ['request-types'],
    () => requestTypeAPI.getRequestTypes().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
    }
  );

  const requestTypes = requestTypesData?.data || requestTypesData || [];

  // Récupérer les statistiques
  const { data: statsData } = useQuery(
    'request-stats',
    () => requestAPI.getStats(),
    {
      refetchInterval: 60000,
    }
  );

  const stats = statsData?.data?.data || {};

  // Forcer un rafraîchissement des demandes à chaque ouverture de la page
  useEffect(() => {
    refetchRequests();
  }, [refetchRequests]);

  // Motifs de traitement pour le rejet (tableRattachement = "requeststreatment")
  const { data: treatmentReasonsData } = useQuery(
    ['request-treatment-reasons'],
    () => reasonsAPI.getReasons({ tableRattachement: 'requeststreatment', isActive: true }).then(res => res.data)
  );

  const treatmentReasons = treatmentReasonsData?.data || treatmentReasonsData || [];

  // Fermer les dropdowns en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mutualisteRef.current && !mutualisteRef.current.contains(event.target)) {
        setShowMutualisteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mutation pour approuver une demande
  const approveMutation = useMutation(
    ({ id, data }) => requestAPI.approveRequest(id, data),
    {
      onSuccess: () => {
        toast.success('Demande approuvée avec succès');
        queryClient.invalidateQueries('admin-requests');
        queryClient.invalidateQueries('request-stats');
        queryClient.invalidateQueries('admin-policies');
        setShowProcessModal(false);
        setSelectedRequest(null);
        setProcessingNotes('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de l\'approbation');
      }
    }
  );

  // Mutation pour rejeter une demande
  const rejectMutation = useMutation(
    ({ id, data }) => requestAPI.rejectRequest(id, data),
    {
      onSuccess: () => {
        toast.success('Demande rejetée avec succès');
        queryClient.invalidateQueries('admin-requests');
        queryClient.invalidateQueries('request-stats');
        setShowProcessModal(false);
        setSelectedRequest(null);
        setProcessingNotes('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors du rejet');
      }
    }
  );

  const handleViewDetails = async (request) => {
    try {
      setSelectedRequest(request);
      setWithdrawalPreview(null);
      setWithdrawalContext(null);
      setShowDetailsModal(true);

      const typeLabel = request.type?.libelle || request.type;
      if (typeLabel === 'Rachat partiel épargne' || typeLabel === 'Rachat total épargne') {
        if (!request.policy?.id) return;

        setIsWithdrawalPreviewLoading(true);
        const savingsResponse = await policyAPI.getPolicySavingsDetails(request.policy.id);
        const savingsData = savingsResponse.data;

        const balance = Number(savingsData?.savingsAccount?.currentBalance || 0);
        const feeRate = Number(savingsData?.policy?.offer?.withdrawalFeeRate || 0);

        // Contexte de compte pour affichage (matricule + titulaire)
        setWithdrawalContext({
          matricule:
            savingsData?.policy?.mutualiste?.matricule ||
            savingsData?.policy?.subscription?.mutualiste?.matricule ||
            null,
          holderLastName:
            savingsData?.policy?.mutualiste?.nom ||
            savingsData?.policy?.subscription?.mutualiste?.nom ||
            null,
          holderFirstNames:
            savingsData?.policy?.mutualiste?.prenoms ||
            savingsData?.policy?.subscription?.mutualiste?.prenoms ||
            null,
        });

        const withdrawalType = typeLabel === 'Rachat total épargne' ? 'TOTAL' : 'PARTIAL';

        let requestedAmount;
        if (withdrawalType === 'TOTAL') {
          requestedAmount = balance;
        } else {
          const metaAmount = request.metadata && typeof request.metadata.amount !== 'undefined'
            ? Number(request.metadata.amount)
            : NaN;
          requestedAmount = !Number.isNaN(metaAmount) ? metaAmount : 0;
        }

        const penalties = requestedAmount * (feeRate / 100);
        const netAmount = requestedAmount - penalties;
        const remainingBalance = balance - netAmount - penalties;

        setWithdrawalPreview({
          withdrawalType,
          requestedAmount,
          feeRate,
          penalties,
          netAmount,
          remainingBalance,
          balance,
        });
        setIsWithdrawalPreviewLoading(false);
      }
    } catch (error) {
      console.error('Erreur lors du calcul du récap de rachat:', error);
      setIsWithdrawalPreviewLoading(false);
      toast.error('Erreur lors du chargement des informations de rachat.');
    }
  };

  const handleProcessRequest = (request, action) => {
    // Le modal de traitement est désormais réservé au rejet (motif obligatoire)
    setSelectedRequest(request);
    setProcessAction(action);
    setProcessingNotes('');
    setTreatmentReasonId('');

    if (action === 'reject') {
      setShowProcessModal(true);
    }
  };

  // Approbation directe sans modal intermédiaire
  const handleApproveDirect = (request) => {
    if (!request) return;

    const data = {
      ...(processingNotes.trim() ? { processingNotes } : {}),
    };

    setSelectedRequest(request);
    approveMutation.mutate({ id: request.id, data });
  };

  const confirmProcess = () => {
    if (processAction === 'reject' && !treatmentReasonId) {
      toast.error('Veuillez sélectionner un motif de rejet');
      return;
    }

    const data = {
      ...(processingNotes.trim() ? { processingNotes } : {}),
      ...(treatmentReasonId ? { treatmentReasonId } : {}),
    };

    if (processAction === 'approve') {
      approveMutation.mutate({ id: selectedRequest.id, data });
    } else {
      rejectMutation.mutate({ id: selectedRequest.id, data });
    }
  };

  // Recherche de mutualistes
  const searchMutualistes = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setMutualistes([]);
      setShowMutualisteDropdown(false);
      return;
    }

    try {
      const response = await mutualisteAPI.searchMutualistes({ search: searchTerm });
      const mutualistesData = response.data?.data?.mutualistes || response.data?.mutualistes || [];
      setMutualistes(mutualistesData);
      setShowMutualisteDropdown(true);
    } catch (error) {
      console.error('Erreur recherche mutualistes:', error);
      setMutualistes([]);
    }
  };

  const handleSelectMutualiste = (mutualiste) => {
    setMutualisteSearch(`${mutualiste.matricule} - ${mutualiste.nom} ${mutualiste.prenoms}`);
    setFilters(prev => ({ ...prev, mutualiste: mutualiste.id }));
    setShowMutualisteDropdown(false);
    setCurrentPage(1);
  };

  // Icône selon le libellé du type de demande (RequestType.libelle)
  const getTypeIcon = (typeLabel) => {
    switch (typeLabel) {
      case 'Résiliation':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'Suspension':
        return <ClockIcon className="h-5 w-5 text-orange-500" />;
      case 'Réactivation':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'Modification':
        return <DocumentTextIcon className="h-5 w-5 text-blue-500" />;
      case 'Rachat partiel épargne':
      case 'Rachat total épargne':
        return <BanknotesIcon className="h-5 w-5 text-emerald-600" />;
      case "Modification du montant de l'épargne":
        return <PencilSquareIcon className="h-5 w-5 text-indigo-500" />;
      case 'Remboursement de ticket modérateur':
      case 'Remboursement de surcoût':
      case 'Remboursement de ticket modérateur et de surcoût':
        // Icône de type hôpital / établissement de soins pour les remboursements santé
        return <BuildingOffice2Icon className="h-5 w-5 text-sky-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  // Libellé affiché pour le type : on retourne simplement le libellé de RequestType
  const getTypeLabel = (typeLabel) => {
    return typeLabel || 'N/A';
  };

  // Couleur de badge selon Status.libelle (statuts configurés dans la table status)
  const getStatusColor = (statusLabel) => {
    switch (statusLabel) {
      case 'En attente': return 'warning';
      case 'Validée': return 'success';
      case 'Rejetée': return 'danger';
      case 'Annulée': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusLabel = (statusLabel) => {
    return statusLabel || '';
  };

  // Texte explicatif du calcul du total demandé pour une demande de remboursement santé
  const getReimbursementCalculationLabel = (healthReq) => {
    if (!healthReq) return '';

    const type = healthReq.reimbursementType;

    if (type === 'Remboursement de surcoût') {
      // 75% du surcoût
      return '75% du surcoût';
    }

    if (type === 'Remboursement de ticket modérateur') {
      // 100% du ticket modérateur
      return '100% du ticket modérateur';
    }

    if (type === 'Remboursement de ticket modérateur et de surcoût') {
      // 75% du surcoût + 100% du ticket modérateur
      return '75% du surcoût + 100% du ticket modérateur';
    }

    return '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Gestion des demandes
        </h1>
        <p className="text-gray-600">
          Traitez les demandes de résiliation, suspension et autres modifications des polices
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <DocumentTextIcon className="h-6 w-6 text-blue-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Total</p>
            <p className="text-xl font-bold text-gray-900">{stats.totalRequests || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <ClockIcon className="h-6 w-6 text-yellow-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">En attente</p>
            <p className="text-xl font-bold text-gray-900">{stats.pendingRequests || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <CheckCircleIcon className="h-6 w-6 text-green-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Approuvées</p>
            <p className="text-xl font-bold text-gray-900">{stats.approvedRequests || 0}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-card p-4">
          <div className="flex flex-col items-center text-center">
            <XCircleIcon className="h-6 w-6 text-red-600 mb-2" />
            <p className="text-xs font-medium text-gray-500 mb-1">Rejetées</p>
            <p className="text-xl font-bold text-gray-900">{stats.rejectedRequests || 0}</p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Filtres</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Date début</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, startDate: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">Date fin</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, endDate: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">Type de demande</label>
            <select
              value={filters.type}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, type: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            >
              <option value="">Tous les types</option>
              {Array.isArray(requestTypes) && requestTypes.map((type) => (
                <option key={type.id} value={type.libelle}>
                  {type.libelle}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setCurrentPage(1);
              }}
              className="form-input"
            >
              <option value="">Tous les statuts</option>
              <option value="PENDING">En attente</option>
              <option value="APPROVED">Approuvées</option>
              <option value="REJECTED">Rejetées</option>
            </select>
          </div>

          <div className="relative" ref={mutualisteRef}>
            <label className="form-label">Mutualiste</label>
            <input
              type="text"
              value={mutualisteSearch}
              onChange={(e) => {
                setMutualisteSearch(e.target.value);
                searchMutualistes(e.target.value);
              }}
              onFocus={() => mutualisteSearch.length >= 2 && setShowMutualisteDropdown(true)}
              placeholder="Rechercher..."
              className="form-input"
            />
            {showMutualisteDropdown && mutualistes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {mutualistes.map((mutualiste) => (
                  <div
                    key={mutualiste.id}
                    onClick={() => handleSelectMutualiste(mutualiste)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <div className="font-medium">{mutualiste.matricule}</div>
                    <div className="text-sm text-gray-600">{mutualiste.nom} {mutualiste.prenoms}</div>
                  </div>
                ))}
              </div>
            )}

      {showAttachmentModal && attachmentPreviewUrl && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75"
              onClick={() => {
                setShowAttachmentModal(false);
                setAttachmentPreviewUrl('');
              }}
            />
            <div className="relative inline-block bg-white rounded-lg shadow-xl transform transition-all sm:max-w-4xl sm:w-full max-h-[90vh]">
              <div className="flex items-start justify-between px-4 pt-4 pb-2 border-b">
                <h3 className="text-base font-medium text-gray-900">Aperçu de la pièce jointe</h3>
                <button
                  onClick={() => {
                    setShowAttachmentModal(false);
                    setAttachmentPreviewUrl('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="px-4 pb-4">
                <div className="w-full h-[70vh] border rounded-md overflow-hidden">
                  <iframe
                    src={attachmentPreviewUrl}
                    title="Pièce jointe"
                    className="w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
          </div>
        </div>
      </div>

      {/* Liste des demandes */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucune demande trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mutualiste</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Police</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(request.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getTypeIcon(request.type?.libelle)}
                        <span className="ml-2 text-sm">{getTypeLabel(request.type?.libelle)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {request.policy?.subscription?.mutualiste?.nom} {request.policy?.subscription?.mutualiste?.prenoms}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.policy?.subscription?.mutualiste?.matricule}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {request.policy?.policyNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge badge-${getStatusColor(request.status?.libelle)}`}>
                        {getStatusLabel(request.status?.libelle)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(request)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-secondary btn-sm"
              >
                Précédent
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))}
                disabled={currentPage === pagination.pages}
                className="btn-secondary btn-sm"
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-gray-700">
                Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{pagination.pages}</span>
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Modal de traitement (toujours au-dessus des autres modales) - uniquement pour le rejet */}
      {showProcessModal && selectedRequest && processAction === 'reject' && (
        <div className="fixed inset-0 z-[70] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowProcessModal(false)} />
            <div className="inline-block bg-white rounded-lg shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Rejeter la demande
                </h3>
                <div className="space-y-4">
                  {processAction === 'reject' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Motif de rejet <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={treatmentReasonId}
                        onChange={(e) => setTreatmentReasonId(e.target.value)}
                        className="form-input"
                      >
                        <option value="">Sélectionner un motif</option>
                        {Array.isArray(treatmentReasons) && treatmentReasons.map((reason) => (
                          <option key={reason.id} value={reason.id}>
                            {reason.libelle}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={processingNotes}
                      onChange={(e) => setProcessingNotes(e.target.value)}
                      rows={3}
                      className="form-input"
                      placeholder={processAction === 'approve' ? 'Notes optionnelles...' : 'Détails sur le motif de rejet'}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={confirmProcess}
                  disabled={rejectMutation.isLoading}
                  className="btn-sm bg-red-600 hover:bg-red-700 text-white"
                >
                  {rejectMutation.isLoading && <LoadingSpinner size="small" className="mr-2" />}
                  Confirmer le rejet
                </button>
                <button
                  onClick={() => setShowProcessModal(false)}
                  className="btn-secondary btn-sm mr-3"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails avec récap spécifique pour les demandes de rachat */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => {
              setShowDetailsModal(false);
              setWithdrawalPreview(null);
              setWithdrawalContext(null);
            }} />
            <div className="inline-block bg-white rounded-lg shadow-xl transform transition-all sm:max-w-3xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Détails de la demande
                  </h3>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setWithdrawalPreview(null);
                      setWithdrawalContext(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Type de demande</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedRequest.type?.libelle || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Statut</p>
                        <span className={`badge badge-${getStatusColor(selectedRequest.status?.libelle || selectedRequest.status)}`}>
                          {getStatusLabel(selectedRequest.status?.libelle || selectedRequest.status)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Date de demande</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDateTime(selectedRequest.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Police concernée</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedRequest.policy?.policyNumber || 'N/A'}
                        </p>
                      </div>

                      {/* Demandes de rachat : afficher le matricule et le titulaire de la police */}
                      {withdrawalContext?.matricule && (
                        <div>
                          <p className="text-xs text-gray-500">Matricule</p>
                          <p className="text-sm font-medium text-gray-900">
                            {withdrawalContext.matricule}
                          </p>
                        </div>
                      )}
                      {withdrawalContext?.holderLastName && (
                        <div>
                          <p className="text-xs text-gray-500">Titulaire de la police</p>
                          <p className="text-sm font-medium text-gray-900">
                            {withdrawalContext.holderLastName} {withdrawalContext.holderFirstNames}
                          </p>
                        </div>
                      )}

                      {/* Demandes de remboursement : matricule + titulaire de la police sur la 3e ligne */}
                      {selectedRequest.healthReimbursementRequest && (
                        <>
                          <div>
                            <p className="text-xs text-gray-500">Matricule</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedRequest.policy?.subscription?.mutualiste?.matricule || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Titulaire de la police</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedRequest.policy?.subscription?.mutualiste
                                ? `${selectedRequest.policy.subscription.mutualiste.prenoms || ''} ${selectedRequest.policy.subscription.mutualiste.nom || ''}`.trim()
                                : 'N/A'}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className={`grid gap-4 ${selectedRequest.status?.libelle === 'Rejetée' || selectedRequest.status === 'REJECTED' ? 'grid-cols-1 md:grid-cols-2' : ''}`}>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Raison de la demande</h4>
                      <p className="text-sm text-gray-600">
                        {selectedRequest.reasonText || 'Aucune raison fournie'}
                      </p>
                    </div>

                    {(selectedRequest.status?.libelle === 'Rejetée' || selectedRequest.status === 'REJECTED') && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Motif de rejet</h4>
                        <p className="text-sm text-gray-600">
                          {(() => {
                            const motif = selectedRequest.treatmentReason?.libelle || 'Non précisé';
                            const notes = selectedRequest.processingNotes;

                            if (!notes) {
                              return motif;
                            }

                            return `${motif} (${notes})`;
                          })()}
                        </p>
                      </div>
                    )}
                  </div>

                  {selectedRequest.notes && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Notes additionnelles</h4>
                      <p className="text-sm text-gray-600">
                        {selectedRequest.notes}
                      </p>
                    </div>
                  )}

                  {(() => {
                    const typeLabel = selectedRequest.type?.libelle || selectedRequest.type;
                    const isWithdrawal = typeLabel === 'Rachat partiel épargne' || typeLabel === 'Rachat total épargne';
                    if (!isWithdrawal) return null;

                    return (
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <h4 className="text-sm font-medium text-purple-900 mb-3">Récapitulatif du rachat</h4>
                        {isWithdrawalPreviewLoading && (
                          <p className="text-sm text-purple-700">Chargement des informations de rachat...</p>
                        )}
                        {!isWithdrawalPreviewLoading && withdrawalPreview && (
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Type de rachat</span>
                              <span className="font-semibold text-gray-900">
                                {withdrawalPreview.withdrawalType === 'TOTAL' ? 'Rachat total' : 'Rachat partiel'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Solde actuel</span>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(withdrawalPreview.balance)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Montant demandé</span>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(withdrawalPreview.requestedAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Pénalités{withdrawalPreview.feeRate ? ` (${withdrawalPreview.feeRate} %)` : ''}
                              </span>
                              <span className="font-semibold text-orange-700">
                                {formatCurrency(withdrawalPreview.penalties)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Montant à recevoir</span>
                              <span className="font-semibold text-green-700">
                                {formatCurrency(withdrawalPreview.netAmount)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Solde après rachat</span>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(withdrawalPreview.remainingBalance)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Récapitulatif spécifique pour les demandes de modification du montant de cotisation épargne */}
                  {selectedRequest.type?.libelle === "Modification du montant de l'épargne" && selectedRequest.metadata && (
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                      <h4 className="text-sm font-medium text-indigo-900 mb-3">Détail de la modification demandée</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant actuel</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(selectedRequest.metadata.currentAmount || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Nouveau montant demandé</span>
                          <span className="font-semibold text-indigo-700">{formatCurrency(selectedRequest.metadata.newAmount || 0)}</span>
                        </div>
                        {selectedRequest.metadata.currentAmount && selectedRequest.metadata.newAmount && (
                          <div className="flex justify-between pt-1 border-t border-indigo-100">
                            <span className="text-gray-600">Variation</span>
                            <span className={`font-bold ${
                              selectedRequest.metadata.newAmount > selectedRequest.metadata.currentAmount
                                ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {selectedRequest.metadata.newAmount > selectedRequest.metadata.currentAmount ? '+' : ''}
                              {(((selectedRequest.metadata.newAmount - selectedRequest.metadata.currentAmount) / selectedRequest.metadata.currentAmount) * 100).toFixed(1)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Récapitulatif spécifique pour les demandes de remboursement santé */}
                  {selectedRequest.healthReimbursementRequest && (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-900 mb-3">Récapitulatif de la demande</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type de prestation</span>
                          <span className="font-semibold text-gray-900">
                            {selectedRequest.healthReimbursementRequest.healthService?.type?.libelle || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Établissement</span>
                          <span className="font-semibold text-gray-900">
                            {selectedRequest.healthReimbursementRequest.healthService?.etablissement || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date de la prestation</span>
                          <span className="font-semibold text-gray-900">
                            {selectedRequest.healthReimbursementRequest.healthService?.executedAt
                              ? new Date(selectedRequest.healthReimbursementRequest.healthService.executedAt).toLocaleDateString('fr-FR')
                              : '-'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant total</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.healthService?.costTotal || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant convientionné</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.healthService?.costConventionne || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Part mutuelle</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.healthService?.assurancePrincipalePart || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ticket modérateur</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.amountTicketModerateur || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Surcoût</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.amountSurcout || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Total demandé
                            {(() => {
                              const calcLabel = getReimbursementCalculationLabel(selectedRequest.healthReimbursementRequest);
                              return calcLabel ? ` (${calcLabel})` : '';
                            })()}
                          </span>
                          <span className="font-semibold text-green-700">
                            {formatCurrency(selectedRequest.healthReimbursementRequest.amountTotalRequested || 0)}
                          </span>
                        </div>

                        {Array.isArray(selectedRequest.healthReimbursementRequest.attachments) &&
                          selectedRequest.healthReimbursementRequest.attachments.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-blue-100">
                              <div className="text-xs font-medium text-blue-900 mb-2">Pièces jointes</div>
                              <div className="flex flex-wrap gap-3">
                                {selectedRequest.healthReimbursementRequest.attachments.map((attachment, index) => (
                                  <button
                                    key={attachment.id || `${attachment.filePath}-${index}`}
                                    type="button"
                                    onClick={() => {
                                      if (!attachment.filePath) return;
                                      const url = getFileUrl(attachment.filePath);
                                      if (url) {
                                        setAttachmentPreviewUrl(url);
                                        setShowAttachmentModal(true);
                                      }
                                    }}
                                    className="inline-flex items-center space-x-1 text-xs text-blue-700 hover:text-blue-900"
                                  >
                                    <DocumentTextIcon className="h-4 w-4" />
                                    <span className="underline">
                                      {attachment.fileName || 'Pièce jointe'}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {(selectedRequest.status?.libelle === 'En attente' || selectedRequest.status === 'PENDING') && (
                    <div className="flex justify-end space-x-2 mt-4">
                      <button
                        onClick={() => handleProcessRequest(selectedRequest, 'reject')}
                        className="btn-sm bg-red-600 hover:bg-red-700 text-white"
                      >
                        Rejeter
                      </button>
                      <button
                        onClick={() => handleApproveDirect(selectedRequest)}
                        className="btn-sm bg-green-600 hover:bg-green-700 text-white flex items-center"
                        disabled={approveMutation.isLoading}
                      >
                        {approveMutation.isLoading && (
                          <LoadingSpinner size="small" className="mr-2" />
                        )}
                        Valider
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequests;
